<template>
  <button
    type="button"
    class="toggle-button"
    :class="{ active: value, small: size === 'small' }"
    role="switch"
    :aria-checked="value.toString()"
    @click="onClick"
  >
    <span aria-hidden="true" :class="{ active: value }" />
  </button>
</template>

<script>
export default {
  props: {
    value: { type: Boolean, default: false },
    size: { type: String, default: '' },
  },
  methods: {
    onClick() {
      this.$emit('input', !this.value);
    },
  },
};
</script>
<style lang="scss" scoped>
// The switch is the smallest place in the product where a state change has to read
// instantly, so the thumb carries the motion and the track carries the colour.
//
// `--toggle-travel` is the distance the thumb moves and `--toggle-direction` its
// sign: Arabic reads the other way, so an "on" thumb has to sit at the *start* of
// the track, which for a transform means travelling negative.
.toggle-button {
  --toggle-travel: 0.9375rem;
  --toggle-direction: 1;

  @apply bg-slate-200 dark:bg-slate-700 flex flex-shrink-0 relative cursor-pointer;
  border: 2px solid transparent;
  border-radius: var(--border-radius-full);
  height: 19px;
  width: 34px;
  transition:
    background-color 200ms cubic-bezier(0.4, 0, 0.2, 1),
    box-shadow 150ms ease-out;

  // Off-state hover only nudges the track; the thumb stays put so hover is never
  // mistaken for a state change.
  &:hover:not(.active) {
    @apply bg-slate-300 dark:bg-slate-600;
  }

  // On. `accent-mint` rather than the fixed mint so the light theme gets the deep
  // readable green and dark gets the alive brand mint.
  &.active {
    @apply bg-accent-mint;
  }

  // The OFF track has to be findable, and a quiet fill is not: measured against the
  // grounds this sits on it is 1.42:1 on the light page and 1.62:1 on a dark card,
  // both far under the 3:1 WCAG 1.4.11 asks of a control's visual boundary. The fix
  // belongs on the boundary, not the fill — `slate-500` clears it in both modes
  // (4.28 / 4.07 light, 4.37 / 3.85 dark) and reads as the hairline the brand wants.
  // The ON state needs none of this: its mint fill is already 12.13:1 on a card.
  //
  // Declared before `:focus-visible` deliberately — the two selectors have identical
  // specificity, so source order is what lets the focus ring replace this hairline
  // with something stronger.
  &:not(.active) {
    box-shadow: inset 0 0 0 1px oklch(var(--slate-500));
  }

  &:focus-visible {
    @apply outline-none ring-2 ring-accent-mint/70 ring-offset-2 ring-offset-white dark:ring-offset-ink;
  }

  &:focus:not(:focus-visible) {
    @apply outline-none;
  }

  &.small {
    --toggle-travel: var(--space-small);

    width: 22px;
    height: 14px;

    span {
      height: var(--space-one);
      width: var(--space-one);
    }
  }

  span {
    // A hairline lift instead of the four stacked shadows this used to carry — the
    // thumb only has to separate from the track, not float above the page.
    @apply bg-white dark:bg-slate-900 inline-block;
    border-radius: var(--border-radius-rounded);
    box-shadow: 0 1px 2px oklch(var(--brand-ink) / 0.28);
    height: 0.9375rem;
    width: 0.9375rem;
    transform: translateX(0);
    // A touch of overshoot: the thumb arrives like a physical switch, not a fade.
    transition: transform 200ms cubic-bezier(0.34, 1.36, 0.64, 1);

    &.active {
      transform: translateX(
        calc(var(--toggle-travel) * var(--toggle-direction))
      );
    }
  }
}

[dir='rtl'] .toggle-button {
  --toggle-direction: -1;
}

@media (prefers-reduced-motion: reduce) {
  .toggle-button,
  .toggle-button span {
    transition: none;
  }
}
</style>
